import { CdkColumnDef } from '@angular/cdk/table';
import { Component, Input, ViewEncapsulation } from '@angular/core';
import { GetReserveReleasesResponse } from '@lsq-frontend/remote-services/enterprise/common';
import { GetReserveReleasesRequest } from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { DateFnsFormat, Modal, Table } from '@lsq-frontend/common-ui';
import { FeatureFlagService, Icons, ResponsiveService } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { format } from 'date-fns';
import {
    IRequestFundsStatusInformation,
    RequestFundsStatus,
    RequestFundsStatusInformation
} from '../../../constants/request-funds-status.constants';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';

@Component({
    selector: 'request-funds-table',
    template: `
        <div class="request-funds-history-card card">
            <div class="card-header pl-0">
                {{ titleNewDashboardEnhancement }}
            </div>
            <div class="card-body">
                <div class="row">
                    <div class="col">
                        <lsq-data-table-mobile
                            *ngIf="isMdBelow$ | async"
                            [dataSource]="dataSource"
                            [isPaginated]="true"
                            [borderlessCards]="false"
                        >
                            <ng-template #mobileCardHeader let-row="row">
                                <div class="mtc-header-wrapper">
                                    <div class="mtc-header-content-left">
                                        <div class="mtc-header-title">
                                            {{ row.amountRequested | currencyDash }}
                                        </div>
                                        <div class="mtc-header-sub-title">
                                            <span>{{
                                                getFormattedDateTimeForMobile(row.dateRequested)
                                            }}</span>
                                        </div>
                                    </div>
                                    <div class="mtc-header-content-right">
                                        <ng-container *ngIf="!!row.reserveReleaseStatus">
                                            <div class="mtc-header-status-text">
                                                <status-pill
                                                    [status]="'statusText'"
                                                    [displayValueMap]="
                                                        getInfoForStatus(row.reserveReleaseStatus)
                                                    "
                                                    [color]="
                                                        getInfoForStatus(row.reserveReleaseStatus)
                                                            .color
                                                    "
                                                ></status-pill>
                                                <lsq-icon-popover
                                                    *ngIf="!(isMdBelow$ | async)"
                                                    [text]="
                                                        getInfoForStatus(row.reserveReleaseStatus)
                                                            .tooltip
                                                    "
                                                >
                                                </lsq-icon-popover>
                                            </div>
                                        </ng-container>
                                    </div>
                                </div>
                            </ng-template>

                            <ng-template #mobileCardColumns let-row="row">
                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ submissionDateHeader }}
                                    </div>
                                    <div class="card-item-value">
                                        {{ getFormattedDateTimeForMobile(row.dateRequested) }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ dateProcessedMobileHeader }}
                                    </div>
                                    <div class="card-item-value">
                                        {{ getFormattedDateTimeForMobile(row.dateProcessed) }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ bankAccountMobileHeader }}
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.bankAccount }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ amountRequestedMobileHeader }}
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.amountRequested | currencyDash }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ amountFundedMobileHeader }}
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.amountFunded | currencyDash }}
                                    </div>
                                </div>
                            </ng-template>

                            <ng-template #mobileCardFooter let-row="row">
                                <ng-container *ngIf="!!row.notes">
                                    <div class="mtc-footer-link">
                                        <lsq-button
                                            appearance="transparent"
                                            (buttonClick)="showNotesModal(row.notes)"
                                            i18n="Table column button to view notes"
                                        >
                                            View Notes
                                        </lsq-button>
                                    </div>
                                </ng-container>
                            </ng-template>
                        </lsq-data-table-mobile>

                        <lsq-data-table
                            *ngIf="!(isMdBelow$ | async)"
                            [dataSource]="dataSource"
                            [layoutFixed]="true"
                            [isPaginated]="true"
                        >
                            <lsq-row-count-column></lsq-row-count-column>

                            <lsq-date-column
                                [column]="{
                                    key: 'dateRequested',
                                    header: dateRequestedHeader,
                                    mobileHeader: 'Submission Date',
                                    sortable: false
                                }"
                                [isDateTimeMultiline]="true"
                            >
                            </lsq-date-column>

                            <lsq-date-column
                                [column]="{
                                    key: 'dateProcessed',
                                    header: dateProcessedHeader,
                                    sortable: false
                                }"
                                [isDateTimeMultiline]="true"
                            >
                            </lsq-date-column>

                            <lsq-column
                                [column]="{
                                    key: 'bankAccount',
                                    header: bankAccountHeader,
                                    truncateTextCss: true,
                                    sortable: false
                                }"
                            ></lsq-column>

                            <lsq-currency-column
                                [column]="{
                                    key: 'amountRequested',
                                    header: amountRequestedHeader,
                                    sortable: false
                                }"
                            ></lsq-currency-column>

                            <lsq-currency-column
                                [column]="{
                                    key: 'amountFunded',
                                    header: amountFundedHeader,
                                    sortable: false
                                }"
                            ></lsq-currency-column>

                            <lsq-spacer-column
                                [column]="{
                                    key: 'spacer1',
                                    width: '2rem'
                                }"
                            ></lsq-spacer-column>

                            <lsq-column
                                [column]="{
                                    key: 'notes',
                                    header: notesHeader,
                                    sortable: false
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <a
                                        *ngIf="!!row.notes"
                                        class="text-bold"
                                        [style.font-size]="'1rem'"
                                        (click)="showNotesModal(row.notes)"
                                        i18n="Table column button to view notes"
                                    >
                                        View Notes
                                    </a>
                                </ng-template>
                            </lsq-column>

                            <lsq-spacer-column
                                [column]="{
                                    key: 'spacer2',
                                    width: '2rem'
                                }"
                            ></lsq-spacer-column>

                            <lsq-column
                                [column]="{
                                    key: 'reserveReleaseStatus',
                                    header: statusHeader,
                                    sortable: false
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <ng-container *ngIf="!!row.reserveReleaseStatus">
                                        <status-pill
                                            [status]="'statusText'"
                                            [displayValueMap]="
                                                getInfoForStatus(row.reserveReleaseStatus)
                                            "
                                            [color]="
                                                getInfoForStatus(row.reserveReleaseStatus).color
                                            "
                                            popover="{{
                                                getInfoForStatus(row.reserveReleaseStatus).tooltip
                                            }}"
                                        ></status-pill>
                                    </ng-container>
                                </ng-template>
                            </lsq-column>
                        </lsq-data-table>
                    </div>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./request-funds-table.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [CdkColumnDef]
})
export class RequestFundsTableComponent {
    @Input() public dataSource: LsqApiServiceListSource<
        GetReserveReleasesResponse,
        GetReserveReleasesRequest
    >;
    public titleNewDashboardEnhancement = this._i18n({
        value: 'Fund Requests',
        meaning: 'fund requests table title'
    });
    public modalTitle = this._i18n({
        value: 'Notes',
        meaning: 'Title for notes modal'
    });
    public icons = Icons;
    public isMdBelow$ = this._responsiveService.mdBelow;

    public submissionDateHeader = this._i18n({
        value: 'Submission Date',
        meaning: 'Table column header'
    });
    public dateRequestedHeader = this._i18n({
        value: 'Date<br />Requested',
        meaning: 'Table column header'
    });
    public dateProcessedHeader = this._i18n({
        value: 'Date<br />Processed',
        meaning: 'Table column header'
    });
    public dateProcessedMobileHeader = this._i18n({
        value: 'Date Processed',
        meaning: 'Table column header'
    });
    public bankAccountHeader = this._i18n({
        value: 'Bank<br />Account',
        meaning: 'Table column header'
    });
    public bankAccountMobileHeader = this._i18n({
        value: 'Bank Account',
        meaning: 'Table column header'
    });
    public amountRequestedHeader = this._i18n({
        value: 'Amount<br />Requested',
        meaning: 'Table column header'
    });
    public amountRequestedMobileHeader = this._i18n({
        value: 'Amount Requested',
        meaning: 'Table column header'
    });
    public amountFundedHeader = this._i18n({
        value: 'Amount<br />Funded',
        meaning: 'Table column header'
    });
    public amountFundedMobileHeader = this._i18n({
        value: 'Amount Funded',
        meaning: 'Table column header'
    });
    public notesHeader = this._i18n({
        value: 'Notes',
        meaning: 'Table column header'
    });
    public statusHeader = this._i18n({
        value: 'Status',
        meaning: 'Table column header'
    });

    constructor(
        private _i18n: I18n,
        public _responsiveService: ResponsiveService,
        public _modalService: ModalService,
        private _featureFlagsService: FeatureFlagService
    ) {}

    public showNotesModal(note: string): void {
        this._modalService.showModal(
            new Modal({
                id: 'funding-request-notes',
                title: this.modalTitle,
                bodyText: note,
                showFooter: false,
                showCloseButton: true
            })
        );
    }

    public getInfoForStatus(status: string): IRequestFundsStatusInformation {
        return RequestFundsStatusInformation[RequestFundsStatus[status]];
    }

    public getFormattedDateTimeForMobile(dateString: string): string {
        if (dateString && `${dateString}`.length > 0) {
            const shortDate = format(dateString, DateFnsFormat.ShortFormat);
            const time = format(dateString, DateFnsFormat.TimeFormat);
            return `${shortDate} at ${time}`;
        } else {
            return Table.NullAmount;
        }
    }
}
